/**
 * Partner Network (subscriber side): pure helpers for the Partner Referrals
 * page. Design: docs/plans/2026-09-26-PARTNER-NETWORK-PORTAL.md.
 *
 * No I/O here, so every branch is unit-tested in
 * utils/__tests__/partnerReferrals.test.ts.
 */

import {
  PARTNER_DESK_CAPABILITIES,
  PARTNER_DESK_TENANT_ROLES,
  PARTNER_NETWORK_TENANT_ROLES,
  partnerDeskCapabilities,
  partnerReferOutEnabled,
  type PartnerDeskCapability,
} from '@valutten/shared-schemas/dist/partnerNetwork';

/**
 * Roles that may open Partner Referrals: the shared allowlist the loyalty
 * callables enforce server-side, so the UI and the server cannot disagree.
 * marketing, service_staff and loyalty_client are deliberately absent;
 * founder does not exist in this app.
 */
export const PARTNER_NETWORK_ROLES: readonly string[] = PARTNER_NETWORK_TENANT_ROLES;

/**
 * The single gate for the REFER-OUT side, in the nav entry, the route guard
 * and the page, with the server's precedence (requirePartnerReferOut):
 * `featureFlags.partner_network` true grants and false denies outright;
 * otherwise the "Referral Partners + Rewards" add-on grant (`addOnGranted`,
 * from useFeatureEntitlement, which asks the server entitlement SSOT; never
 * from the tier) grants. The role must be on the allowlist either way, and
 * the tenant's refer-out switch must not be off (partnerReferOutEnabled): a
 * tenant that only runs its own program sees just "My referral program".
 * The loyalty-codebase callables re-check all of it server-side.
 */
export function canAccessPartnerNetwork(
  role: string | null | undefined,
  featureFlags: Record<string, unknown> | null | undefined,
  addOnGranted = false
): boolean {
  if (!role || !PARTNER_NETWORK_ROLES.includes(role)) return false;
  if (!partnerReferOutEnabled(featureFlags)) return false;
  const flag = featureFlags?.partner_network;
  if (flag === true) return true;
  if (flag === false) return false;
  return addOnGranted === true;
}

/** The company id to ask the entitlement SSOT for, or undefined when the flag already decides (no RPC). */
export function partnerNetworkEntitlementCompanyId(
  companyData: { id?: string; companyId?: string; featureFlags?: Record<string, unknown> | null } | null | undefined
): string | undefined {
  const flag = companyData?.featureFlags?.partner_network;
  if (typeof flag === 'boolean') return undefined;
  return companyData?.id || companyData?.companyId || undefined;
}

export type BudgetParse =
  | { ok: true; cents: number | null }
  | { ok: false; error: string };

/**
 * A budget typed in dollars ("780000", "$780,000", "780000.50") to integer
 * cents via Math.round(dollars * 100). Blank means "not given" (null). At most
 * two decimal places; zero and negatives are refused because the schema
 * requires a positive budget when one is given.
 */
export function parseBudgetDollarsToCents(input: string): BudgetParse {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (cleaned === '') return { ok: true, cents: null };
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return { ok: false, error: 'Enter the budget in dollars, for example 780000' };
  }
  const cents = Math.round(Number(cleaned) * 100);
  if (!Number.isSafeInteger(cents) || cents <= 0) {
    return { ok: false, error: 'Enter a budget above $0, or leave it blank' };
  }
  return { ok: true, cents };
}

/** Money is formatted only at presentation (root CLAUDE.md §6). */
export function formatCentsAud(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

/**
 * Firestore Timestamps cross the callable boundary as plain JSON, and the
 * shape depends on the serializer: `{ _seconds, _nanoseconds }` (Admin SDK
 * Timestamp JSON) or `{ seconds, nanoseconds }`. ISO strings, epoch millis and
 * Date are accepted too. Anything unrecognised returns null, never NaN.
 */
export function timestampToMillis(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    const seconds = typeof v._seconds === 'number' ? v._seconds : v.seconds;
    const nanos = typeof v._nanoseconds === 'number' ? v._nanoseconds : v.nanoseconds;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      const n = typeof nanos === 'number' && Number.isFinite(nanos) ? nanos : 0;
      return seconds * 1000 + Math.floor(n / 1_000_000);
    }
  }
  return null;
}

/** "26 Sep 2026", or an em dash placeholder when the value is missing or unreadable. */
export function formatReferralDate(value: unknown, timeZone?: string): string {
  const ms = timestampToMillis(value);
  if (ms === null) return '—';
  return new Date(ms).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * Callable failure to a sentence a broker can act on. The Firebase web SDK
 * prefixes codes with `functions/`. Server messages are shown only for codes
 * whose messages the loyalty callables author for end users.
 */
export function referralErrorMessage(err: unknown): string {
  const e = (err ?? {}) as { code?: unknown; message?: unknown };
  const code = typeof e.code === 'string' ? e.code.replace(/^functions\//, '') : '';
  const serverMessage = typeof e.message === 'string' ? e.message.trim() : '';
  switch (code) {
    case 'already-exists':
      return 'This client has already been referred to this partner.';
    case 'permission-denied':
      // The one refusal whose server wording helps: the chosen broker is outside the caller's scope.
      if (serverMessage === 'You cannot refer on behalf of this broker.') return serverMessage;
      return 'Your account cannot send partner referrals. Ask your administrator if you think this is wrong.';
    case 'failed-precondition':
      return serverMessage || 'This referral cannot be sent right now.';
    case 'invalid-argument':
      return serverMessage ? `Please check the form. ${serverMessage}` : 'Please check the form and try again.';
    case 'unauthenticated':
      return 'Your session has expired. Sign in again, then resend the referral.';
    case 'not-found':
      return 'Partner referrals are not available yet. Please try again later.';
    case 'unavailable':
    case 'deadline-exceeded':
      return 'The referral service is temporarily unavailable. Please try again in a moment.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

// ─── My referral program (the desk of a program this tenant operates) ───────
// docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §16. A referral program a
// VALUTTEN tenant runs (partner doc `operatingCompanyId` = the company, with
// the Referral Partners add-on) is worked from inside VALUTTEN, under Partner
// Referrals, as "My referral program". A standalone partner (e.g. Inspire)
// keeps its own partner portal; nothing here reaches it.

/** Where the desk is mounted in this app's router. */
export const MY_PROGRAM_PATH = '/partner-referrals/program';

/**
 * Roles that work a tenant-operated program's desk: super_admin and
 * administrator (the full desk) and marketing (the referral queue only).
 * The shared allowlist the loyalty desk callables enforce.
 */
export const PARTNER_DESK_ROLES: readonly string[] = PARTNER_DESK_TENANT_ROLES;

/**
 * Whether to ASK the server for this caller's programs at all: a desk role
 * plus the same flag/add-on precedence as canAccessPartnerNetwork. A true
 * answer shows nothing by itself; "My referral program" appears only when
 * partnerDeskGetContext then lists at least one program.
 */
export function canOpenPartnerDesk(
  role: string | null | undefined,
  featureFlags: Record<string, unknown> | null | undefined,
  addOnGranted = false
): boolean {
  if (!role || !PARTNER_DESK_ROLES.includes(role)) return false;
  const flag = featureFlags?.partner_network;
  if (flag === true) return true;
  if (flag === false) return false;
  return addOnGranted === true;
}

/**
 * The single predicate for the Partner Referrals side-nav item and its route
 * guard once the desk exists: the referrer side (canAccessPartnerNetwork), or
 * a desk role whose company operates at least one active program. marketing
 * reaches Partner Referrals only through the second branch.
 */
export function canSeePartnerReferrals(
  role: string | null | undefined,
  featureFlags: Record<string, unknown> | null | undefined,
  addOnGranted: boolean,
  hasDeskProgram: boolean
): boolean {
  if (canAccessPartnerNetwork(role, featureFlags, addOnGranted)) return true;
  return hasDeskProgram && canOpenPartnerDesk(role, featureFlags, addOnGranted);
}

/**
 * Server capability (shared-schemas PARTNER_DESK_CAPABILITIES, what
 * enforceDeskAccess checks) to the desk UI capabilities that offer it
 * (@valutten/partner-desk-ui DESK_CAPABILITIES). `crm.stageMap` needs both
 * crm.link and settings.manage, as partnerUpdatePlatformSettings does.
 */
const DESK_UI_CAPABILITIES_FOR: Readonly<Record<PartnerDeskCapability, readonly string[]>> = {
  'referrals.read': ['referrals.view'],
  'referrals.verify': ['referrals.verify'],
  'referrals.invite': ['referrals.inviteReferrer'],
  'referrals.reject': ['referrals.reject', 'referrals.resolveDuplicate'],
  'referrals.stage': ['referrals.move'],
  'referrals.price': [],
  'members.read': [],
  'members.manage': ['members.manage'],
  'applications.read': ['applications.view'],
  'applications.manage': ['applications.decide'],
  'fees.read': ['fees.view'],
  'fees.manage': ['fees.edit'],
  'payouts.read': ['payouts.view'],
  'payouts.manage': ['payouts.manage'],
  'settings.read': ['settings.view'],
  'settings.manage': ['settings.edit'],
  'crm.link': ['crm.link'],
};

/** Desk UI capabilities that show fee or payout figures. */
const FEE_FIGURE_CAPABILITIES: readonly string[] = ['fees.view', 'fees.edit', 'payouts.view', 'payouts.manage'];

/**
 * The desk UI capabilities for a tenant viewer. The server's list
 * (partnerDeskGetContext) is intersected with this app's own copy of the role
 * table, so neither a stale server nor a stale client can widen the desk; the
 * server still checks every call. A hideCommissions viewer gets no fee or
 * payout screens (the server nulls those figures for them anyway).
 */
export function tenantDeskUiCapabilities(
  role: string | null | undefined,
  serverCapabilities: unknown,
  hideCommissions: boolean
): string[] {
  const allowed = new Set<string>(partnerDeskCapabilities('tenant', role));
  const server = new Set<string>(
    Array.isArray(serverCapabilities) ? serverCapabilities.filter((c): c is string => typeof c === 'string') : []
  );
  const held = (c: PartnerDeskCapability) => allowed.has(c) && server.has(c);
  const out = new Set<string>();
  for (const c of PARTNER_DESK_CAPABILITIES) {
    if (held(c)) for (const ui of DESK_UI_CAPABILITIES_FOR[c]) out.add(ui);
  }
  if (held('crm.link') && held('settings.manage')) out.add('crm.stageMap');
  if (hideCommissions) for (const c of FEE_FIGURE_CAPABILITIES) out.delete(c);
  return Array.from(out).sort();
}

/** One program in partnerDeskGetContext().programs. */
export interface DeskProgramRef {
  partnerId: string;
  name: string;
}

/** The programs list from partnerDeskGetContext, defensively read (bad rows dropped). */
export function readDeskPrograms(value: unknown): DeskProgramRef[] {
  const list = (value as { programs?: unknown } | null | undefined)?.programs;
  if (!Array.isArray(list)) return [];
  const out: DeskProgramRef[] = [];
  for (const p of list) {
    const partnerId = (p as { partnerId?: unknown } | null)?.partnerId;
    const name = (p as { name?: unknown } | null)?.name;
    if (typeof partnerId !== 'string' || !partnerId) continue;
    out.push({ partnerId, name: typeof name === 'string' && name.trim() ? name : partnerId });
  }
  return out;
}

/**
 * Codes partnerDeskGetContext uses to say "no desk for you" (wrong role, no
 * add-on, founder): the item stays hidden rather than showing an error.
 */
export function isNoDeskError(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  const c = typeof code === 'string' ? code.replace(/^functions\//, '') : '';
  return c === 'permission-denied' || c === 'not-found' || c === 'failed-precondition' || c === 'unauthenticated';
}

/** tenantGetPartnerReferralDealCandidates row (client-functions). */
export interface TenantDealCandidate {
  dealId: string;
  provider: string;
  borrowerName: string | null;
  status: string | null;
  /** 'YYYY-MM' or null. */
  settlementMonth: string | null;
  adviserName: string | null;
}

/** The candidate rows, defensively read (a row without a deal id is dropped). */
export function readDealCandidates(value: unknown): TenantDealCandidate[] {
  const list = (value as { candidates?: unknown } | null | undefined)?.candidates;
  if (!Array.isArray(list)) return [];
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
  const out: TenantDealCandidate[] = [];
  for (const row of list) {
    const r = (row ?? {}) as Record<string, unknown>;
    const dealId = str(r.dealId);
    if (!dealId) continue;
    const month = str(r.settlementMonth);
    out.push({
      dealId,
      provider: str(r.provider) ?? 'crm',
      borrowerName: str(r.borrowerName),
      status: str(r.status),
      settlementMonth: month && /^\d{4}-\d{2}$/.test(month) ? month : null,
      adviserName: str(r.adviserName),
    });
  }
  return out;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** '2026-09' -> 'Sep 2026'; anything else -> null. Display only. */
export function formatSettlementMonth(month: string | null | undefined): string | null {
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return null;
  const [y, m] = month.split('-').map(Number);
  if (m < 1 || m > 12) return null;
  // Fixed names, not toLocaleDateString: ICU builds disagree on 'Sep' vs 'Sept'.
  return `${MONTH_SHORT[m - 1]} ${y}`;
}

/**
 * Desk callable failure to a sentence. The loyalty desk callables and the
 * candidates callable author their failed-precondition, permission-denied and
 * invalid-argument text for users (for example "Find the deal again from this
 * referral, then link it."), so that text is shown as is.
 */
export function deskErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const e = (err ?? {}) as { code?: unknown; message?: unknown };
  const code = typeof e.code === 'string' ? e.code.replace(/^functions\//, '') : '';
  const message = typeof e.message === 'string' ? e.message.trim() : '';
  switch (code) {
    case 'failed-precondition':
    case 'permission-denied':
    case 'invalid-argument':
      return message || fallback;
    case 'resource-exhausted':
      return 'Too many lookups in a short time. Wait a minute, then try again.';
    case 'not-found':
      return 'This referral is no longer available.';
    case 'unauthenticated':
      return 'Your session has expired. Sign in again.';
    case 'unavailable':
    case 'deadline-exceeded':
      return 'The service is temporarily unavailable. Please try again in a moment.';
    default:
      return fallback;
  }
}
